import { Link } from "react-router-dom";
import { ArrowRight, Euro, CalendarX, HandCoins } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageTitle } from "@/lib/usePageTitle";
import { FAQS, FEES } from "@/lib/content";

const FeesFaq = () => {
  usePageTitle("Fees & FAQ", "Session fees, low-cost options and frequently asked questions about psychotherapy and counselling with Brian Boland.");
  return (
    <div data-testid="fees-faq-page">
      <section className="mx-auto max-w-6xl px-5 pb-16 pt-16 sm:px-8 lg:pt-24">
        <Reveal y={12}><p className="eyebrow">Fees & Frequently asked questions</p></Reveal>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.12] tracking-tight text-[#1E2723] sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.15}>Clear, honest answers</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#527363]">before you begin</MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-[#4A5750] sm:text-lg">
            Starting therapy brings practical questions as well as personal ones. Here is what to expect around fees, sessions and how everything works.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-24 sm:px-8" aria-labelledby="fees-heading">
        <Reveal>
          <h2 id="fees-heading" className="font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl">Fees</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#54645B]">
            Transparent, per-session fees — in person at the practice in Marino, or online wherever you are in the world.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {FEES.map((f, i) => (
            <Reveal key={f.title} delay={0.1 * i}>
              <div className="soft-card flex h-full flex-col p-7" data-testid={`fee-card-${i}`}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#F8EBE7] text-[#B85C43]">
                  {[Euro, CalendarX, HandCoins][i] && (() => { const I = [Euro, CalendarX, HandCoins][i]; return <I size={22} strokeWidth={1.75} />; })()}
                </span>
                <h3 className="mt-5 text-lg font-semibold text-[#1E2723]">{f.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-[#54645B]">{f.detail}</p>
                <p className="mt-4 rounded-full bg-[#E3ECE7] px-4 py-2 text-center text-xs font-semibold uppercase tracking-wider text-[#527363]">{f.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-[#54645B]">
            <strong className="text-[#2A3630]">Cancellations:</strong> please give at least 48 hours’ notice where possible; sessions cancelled with less notice may be charged. Payment and cancellation details are confirmed in full when you book.
          </p>
        </Reveal>
      </section>

      <section className="bg-[#F2ECE1] py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <h2 id="faq-heading" className="text-center font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl lg:text-4xl">
              Frequently asked questions
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-12 space-y-4" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="soft-card border-none px-6" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="py-5 text-left text-base font-semibold text-[#1E2723] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-[#54645B]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-12 text-center">
              <p className="text-sm text-[#54645B]">Still have a question?</p>
              <Link to="/booking" data-testid="faq-book-cta" className="btn-primary mt-4">
                Ask in confidence <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default FeesFaq;
